"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.objectMatchesSearch = void 0;
const escape_string_regexp_1 = __importDefault(require("escape-string-regexp"));
const fast_deep_equal_1 = __importDefault(require("fast-deep-equal"));
const query_generation_1 = require("./query-generation");
// Slight abuse of the query generator, to make use of its well-developed
// ability to walk the search tree (which we need to do this anyways)
class BooleanSubqueryGenerator {
    _target;
    constructor(_target) {
        this._target = _target;
    }
    emptySearchQuery = {
        result: true,
    };
    andKey = 'and';
    orKey = 'or';
    notKey = 'not';
    idKey = 'id';
    _getValueFromKeyPath(key) {
        // Split on dots by default (as if they were nested sub-keys of
        // an object) but allow escaping them for attribute names
        // containing dots
        // avoid lookbehind
        const keyParts = key.match(/[^\\]\./) ? key.split('.') : [key];
        return keyParts.reduce((current, part) => {
            if (current === null) {
                return null;
            }
            return current.hasOwnProperty(part) ? current[part] : null;
        }, this._target);
    }
    gteSubquery(key, value) {
        return { result: this._getValueFromKeyPath(key) >= value };
    }
    lteSubquery(key, value) {
        return { result: this._getValueFromKeyPath(key) <= value };
    }
    gtSubquery(key, value) {
        return { result: this._getValueFromKeyPath(key) > value };
    }
    ltSubquery(key, value) {
        return { result: this._getValueFromKeyPath(key) < value };
    }
    beforeSubquery(key, value) {
        const actual = new Date(this._getValueFromKeyPath(key));
        return { result: actual < new Date(value) };
    }
    afterSubquery(key, value) {
        const actual = new Date(this._getValueFromKeyPath(key));
        return { result: actual > new Date(value) };
    }
    inSubquery(key, value) {
        // Mimics the behaviour of Mongo's $in
        const actual = this._getValueFromKeyPath(key);
        const result = value.some((possible) => {
            if (actual instanceof Array) {
                return actual.includes(possible);
            }
            return actual === possible;
        });
        return { result };
    }
    notInSubquery(key, value) {
        return { result: !this.inSubquery(key, value).result };
    }
    likeSubquery(key, value) {
        const actual = this._getValueFromKeyPath(key);
        const pattern = new RegExp((0, escape_string_regexp_1.default)(value), 'i');
        return { result: pattern.test(actual) };
    }
    notLikeSubquery(key, value) {
        return { result: !this.likeSubquery(key, value).result };
    }
    equalsSubquery(key, value) {
        const actual = this._getValueFromKeyPath(key);
        return { result: (0, fast_deep_equal_1.default)(actual, value) };
    }
    notEqualsSubquery(key, value) {
        return { result: !this.equalsSubquery(key, value).result };
    }
    valueIsEmptySubquery(key, empty) {
        const actual = this._getValueFromKeyPath(key);
        if (empty) {
            return {
                result: actual === null || actual === undefined || actual === '',
            };
        }
        return {
            result: actual !== null && actual !== undefined && actual !== '',
        };
    }
}
const checkSubsearchResult = (result) => {
    let base;
    let andResult;
    let orResult;
    let notResult;
    if (result.result !== undefined) {
        base = result.result;
    }
    if (result.and) {
        andResult = result.and.reduce((sub, current) => sub && checkSubsearchResult(current), true);
    }
    if (result.or) {
        orResult = result.or.reduce((sub, current) => sub || checkSubsearchResult(current), false);
    }
    if (result.not) {
        notResult = !result.not.reduce((sub, current) => sub && checkSubsearchResult(current), true);
    }
    return [base, andResult, orResult, notResult]
        .filter((res) => res !== undefined)
        .reduce((current, res) => current && res, true);
};
const objectMatchesSearch = (subject, params) => {
    const queryGen = new query_generation_1.QueryGenerator(new BooleanSubqueryGenerator(subject));
    const baseResult = queryGen.generateQuery(params);
    return checkSubsearchResult(baseResult);
};
exports.objectMatchesSearch = objectMatchesSearch;
